import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { CompanyWatermark, StreemWatermark, styled } from '@streem/ui-react';
import { streem } from 'streem-sdk-protobuf';
import { VideoFrameMobile } from '../video_frame/video_frame_mobile';
import { Controls } from '../../lobby/controls';
import { useRoomSession } from '../../../context/room_session';
import { useCompanySettings } from '../../../context/company_settings';
import { SuccessfulStreemshot } from '../successful_streemshot';
import { useObservable, useVideoEnabled } from '../../..';
import { isRoomSession, StreemError } from '@streem/sdk-core';
import { GpsResponse } from './gps_response/gps_response';
import { RequestCameraFlip } from './request_camera_flip';
import { useCompanyInfo } from '../../../context/company_info';
import { ExpertAvatar } from './expert_avatar';
import { DeviceTroubleshootingModal } from '../../device_troubleshooting/device_troubleshooting_modal';
import { takeFirstEmitted } from '../../../util/take_first_emitted';
import { useHostVideoTrackRestartListener } from '../../../hooks/use_host_video_track_restart_listener';
import LostConnectionModal from '../../lobby/lost_connection_modal';
import { EndingCallScreen } from './ending_call_screen';
export function HostCallFrame({ onCallEndedByHost = () => { } }) {
    const roomSession = useRoomSession();
    const companyInfo = useCompanyInfo();
    const companySettings = useCompanySettings();
    const [roomInvitationStatus] = useObservable(roomSession.roomInvitationStatus);
    const [hostAudioConnected] = useObservable(roomSession.media.localAudio.connected);
    const [localVideoController] = useObservable(roomSession.media.localVideoController);
    const [audioState] = useObservable(roomSession.media.localAudio.enabled);
    const [videoState = false] = useVideoEnabled(roomSession.media.localVideoController);
    const [videoControllers] = useObservable(roomSession.media.videoControllers);
    const [hasRemoteUserConnection] = useObservable(roomSession.media.hasRemoteUserConnection);
    const [trackProviderConnectionState] = useObservable(roomSession.media.connectionState);
    const [remoteParticipantStatus] = useObservable(roomSession.room.remoteParticipantStatus);
    const [roomSessionStatus] = useObservable(roomSession.status);
    const [callStartTime] = useObservable(roomSession.bookmarks.callStartTime);
    const [userMediaVideoError] = useObservable(roomSession.media.getUserMediaVideoError);
    const [userMediaAudioError] = useObservable(roomSession.media.getUserMediaAudioError);
    const [cameraFlipOpen, setCameraFlipOpen] = useState(false);
    const [leavingCall, setLeavingCall] = useState(false);
    const [showLostConnectionMessage, setShowLostConnectionMessage] = useState(false);
    const [deviceTroubleshootingModalConfig, setDeviceTroubleshootingModalConfig] = useState({ deviceType: 'Microphone', showDeviceTroubleshootingModal: false });
    useEffect(() => {
        if (userMediaAudioError === 'NotAllowedError') {
            setDeviceTroubleshootingModalConfig({
                deviceType: 'Microphone',
                showDeviceTroubleshootingModal: true,
            });
        }
        else if (userMediaVideoError === 'NotAllowedError') {
            setDeviceTroubleshootingModalConfig({
                deviceType: 'Camera',
                showDeviceTroubleshootingModal: true,
            });
        }
    }, [userMediaAudioError, userMediaVideoError]);
    useEffect(() => {
        if (!callStartTime)
            return;
        const lostConnectionTimeoutId = setTimeout(() => {
            setShowLostConnectionMessage(true);
        }, 1000);
        return () => clearTimeout(lostConnectionTimeoutId);
    }, [callStartTime]);
    const handleEndCallByHost = () => {
        setLeavingCall(true);
        onCallEndedByHost();
    };
    useHostVideoTrackRestartListener(roomSession);
    if (!isRoomSession(roomSession)) {
        throw new StreemError('Unable to provide in-call experience without a RoomSession.');
    }
    const videoFrames = useMemo(() => {
        if (!videoControllers || !videoControllers.length) {
            return undefined;
        }
        return videoControllers.map(controller => {
            // right now, all the host is always on mobile... so VideoFrameMobile is fine.
            return _jsx(VideoFrameMobile, { videoController: controller }, controller.id);
        });
    }, [videoControllers]);
    useEffect(() => {
        const setBackgrounded = async (backgrounded) => {
            const localVideo = await takeFirstEmitted(roomSession.media.localVideoController);
            await localVideo?.setBackgrounded(backgrounded);
            // Note: this sets localAudio to backgrounded, but the mic may or may not be available when the
            //       browser tab is backgrounded.  This code should check to see if the mic is available
            //       before setting it to backgrounded, but I'm not sure if that's possible.
            roomSession.media.localAudio.setBackgrounded(backgrounded);
        };
        const checkIfBackgrounded = () => {
            if (document.visibilityState === 'visible') {
                setBackgrounded(false);
            }
            else {
                setBackgrounded(true);
            }
        };
        document.addEventListener('visibilitychange', checkIfBackgrounded);
        return () => {
            window.removeEventListener('visibilityChange', checkIfBackgrounded);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // For 2 way video, wait for the spectator video track to be available.  For 1 way video,
    //    the spectator video track is not displayed
    const waitingForSpectatorVideo = companySettings?.faceToFaceModeEnabled &&
        !videoControllers?.some(controller => !controller.ownerIsLocalUser);
    // Consider the spectator present if:
    //    1. The room invitation has been accepted
    //    2. We are not waiting for the spectator video to connect
    //    3. There is at least one remote user connected
    //    4. The remote user is active
    const spectatorInCall = roomInvitationStatus === 'ACCEPTED' &&
        !waitingForSpectatorVideo &&
        hasRemoteUserConnection &&
        remoteParticipantStatus === streem.api.RoomParticipant.Status.STATUS_ACTIVE;
    const callEndingOrEnded = roomSessionStatus === 'DISCONNECTING' ||
        roomSessionStatus === 'DISCONNECTED' ||
        leavingCall;
    const isReconnectingToTrackProvider = trackProviderConnectionState === 'RECONNECTING';
    return (_jsxs(_Fragment, { children: [showLostConnectionMessage &&
                roomSessionStatus === 'CONNECTED' &&
                isReconnectingToTrackProvider &&
                !leavingCall && _jsx(LostConnectionModal, {}), deviceTroubleshootingModalConfig.showDeviceTroubleshootingModal && (_jsx(DeviceTroubleshootingModal, { permissionDenied: userMediaAudioError === 'NotAllowedError' ||
                    userMediaVideoError === 'NotAllowedError', handleContinue: () => setDeviceTroubleshootingModalConfig({
                    deviceType: 'Microphone',
                    showDeviceTroubleshootingModal: false,
                }), inCall: true, device: deviceTroubleshootingModalConfig.deviceType })), _jsxs(Container, { children: [_jsx(Top, { className: 'fade-in', children: companySettings?.watermarkUrl ? (_jsx(CompanyWatermark, { src: companySettings.watermarkUrl, onError: event => (event.target.style.display = 'none') })) : (_jsx(StreemWatermark, {})) }), _jsx(RequestCameraFlip, { open: cameraFlipOpen, setOpen: setCameraFlipOpen }), _jsx(GpsResponse, {}), !spectatorInCall && !callEndingOrEnded && (_jsx(ExpertAvatar, { className: 'fade-in', companyInfo: companyInfo })), callEndingOrEnded && !hasRemoteUserConnection && _jsx(EndingCallScreen, {}), _jsx(SuccessfulStreemshot, {}), spectatorInCall && videoFrames, _jsxs(Bottom, { className: 'fade-in', children: [!cameraFlipOpen && (_jsx(Controls, { inCall: true, onEndCallButtonClicked: handleEndCallByHost, audioState: {
                                    notAvailable: !hostAudioConnected,
                                    showMicTroubleshootingMessage: () => {
                                        setDeviceTroubleshootingModalConfig({
                                            deviceType: 'Microphone',
                                            showDeviceTroubleshootingModal: true,
                                        });
                                    },
                                    enabled: audioState ?? false,
                                    toggleEnabled: () => {
                                        roomSession.media.localAudio.setEnabled(!audioState);
                                    },
                                }, videoState: {
                                    enabled: videoState ?? false,
                                    toggleEnabled: () => {
                                        if (localVideoController) {
                                            localVideoController.setEnabled(!videoState);
                                        }
                                    },
                                } })), _jsx(ButtonGradient, {})] })] })] }));
}
const Top = styled.div({
    position: 'absolute',
    top: '5vh',
    display: 'flex',
    width: '100vw',
    justifyContent: 'center',
    zIndex: 3,
});
const Bottom = styled.div `
    position: absolute;
    bottom: 15px;
    left: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    @media (orientation: landscape) {
        position: fixed;
        bottom: 5px;
    }
`;
const Container = styled.div(({ theme }) => `
    width: 100%;
    background-color: ${theme.colors.black};
    display: flex;

    @keyframes fadeInAnimation {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }

    .fade-in {
        animation: fadeInAnimation ease 2s;
        animation-iteration-count: 1;
        animation-fill-mode: forwards;
    }

    z-index: ${theme.layers.callFrame}
`);
const ButtonGradient = styled.div `
    background: linear-gradient(
        0deg,
        hsla(0, 0%, 0%, 0.6) 0%,
        hsla(0, 0%, 0%, 0.021) 91%,
        hsla(0, 0%, 0%, 0.008) 95.2%,
        hsla(0, 0%, 0%, 0.002) 98.2%,
        hsla(0, 0%, 0%, 0) 100%
    );
    mix-blend-mode: multiply;
    width: 100%;
    height: 140px;
    position: fixed;
    bottom: 0;
    z-index: 0;
`;
//# sourceMappingURL=host_call_frame.js.map